import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, LinkSimple, Copy, ArrowsClockwise, Trash, FilePdf, UserCircleCheck, UserCircleMinus } from "@phosphor-icons/react";
import AnamnesiTab from "@/components/app/AnamnesiTab";
import MeasurementsTab from "@/components/app/MeasurementsTab";
import PhasesTab from "@/components/app/PhasesTab";
import WorkoutsTab from "@/components/app/WorkoutsTab";
import PhotosTab from "@/components/app/PhotosTab";
import LogbookTab from "@/components/app/LogbookTab";
import BadgesTab from "@/components/app/BadgesTab";
import CheckinTab from "@/components/app/CheckinTab";
import OverviewTab from "@/components/app/OverviewTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-link support: ?tab=checkin lands directly on the Check-in tab
  // (used by the Dashboard "DA LEGGERE" section).
  const initialTab = searchParams.get("tab") || "panoramica";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareToken, setShareToken] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [showSharePanel, setShowSharePanel] = useState(false);

  const handleTabChange = (val) => {
    setActiveTab(val);
    // Once the user manually picks a tab we drop the deep-link param so
    // refresh/back doesn't keep redirecting them.
    if (searchParams.get("tab")) {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
  };

  const fetchClient = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients/${clientId}`);
      setClient(data);
      if (data.share_token) setShareToken(data.share_token);
    } catch (err) {
      toast.error("Cliente non trovato");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  }, [clientId, navigate]);

  useEffect(() => { fetchClient(); }, [fetchClient]);

  const generateShareLink = async () => {
    setShareLoading(true);
    try {
      const { data } = await axios.post(`${API}/clients/${clientId}/share-link`);
      setShareToken(data.share_token);
      toast.success("Link generato");
    } catch {
      toast.error("Errore nella generazione del link");
    } finally {
      setShareLoading(false);
    }
  };

  const revokeShareLink = async () => {
    setShareLoading(true);
    try {
      await axios.delete(`${API}/clients/${clientId}/share-link`);
      setShareToken(null);
      toast.success("Link revocato");
    } catch {
      toast.error("Errore nella revoca del link");
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/shared/${shareToken}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiato negli appunti");
  };

  const [pdfLoading, setPdfLoading] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  const toggleActive = async () => {
    if (togglingActive) return;
    const next = client.is_active === false; // currently inactive → activate
    setTogglingActive(true);
    try {
      const { data } = await axios.put(`${API}/clients/${clientId}`, { is_active: next });
      setClient(data);
      toast.success(
        next
          ? "Cliente riattivato: tornerà nei contatori e notifiche."
          : "Cliente segnato come inattivo. Dati conservati, escluso da contatori e notifiche."
      );
    } catch {
      toast.error("Errore nell'aggiornamento dello stato cliente");
    } finally {
      setTogglingActive(false);
    }
  };

  const exportPdfReport = async () => {
    if (pdfLoading) return;
    setPdfLoading(true);
    try {
      const response = await axios.get(`${API}/clients/${clientId}/report.pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const fname = `report_${client?.first_name || ""}_${client?.last_name || ""}_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.setAttribute("download", fname.replace(/\s+/g, "_"));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report PDF scaricato");
    } catch (err) {
      toast.error("Errore nella generazione del PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!client) return null;

  return (
    <div data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/clients")}
            className="text-zinc-400 hover:text-white hover:bg-[#1A1A1A] p-2"
            data-testid="back-to-clients"
          >
            <ArrowLeft size={20} />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#007AFF]/20 flex items-center justify-center text-[#007AFF] font-bold text-lg">
              {client.first_name?.charAt(0)?.toUpperCase()}{client.last_name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <h1
                className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white flex items-center gap-2 flex-wrap"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {client.first_name} {client.last_name}
                {client.is_active === false && (
                  <span
                    className="text-[10px] px-2 py-0.5 bg-zinc-700/40 text-zinc-400 rounded-full font-medium normal-case tracking-normal"
                    data-testid="client-inactive-badge"
                  >
                    inattivo
                  </span>
                )}
              </h1>
              {client.email && <p className="text-zinc-500 text-sm">{client.email}</p>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={toggleActive}
            disabled={togglingActive}
            className={`border-[#262626] gap-2 ${
              client.is_active === false
                ? "text-[#34C759] hover:bg-[#34C759]/10 hover:text-[#34C759]"
                : "text-zinc-400 hover:bg-[#1A1A1A] hover:text-white"
            }`}
            data-testid="toggle-client-active"
            title={
              client.is_active === false
                ? "Riattiva: torna nei contatori e notifiche"
                : "Segna come inattivo: conserva i dati ma esclude dai contatori e notifiche"
            }
          >
            {client.is_active === false ? (
              <UserCircleCheck size={18} weight="duotone" />
            ) : (
              <UserCircleMinus size={18} />
            )}
            <span className="hidden sm:inline">
              {client.is_active === false ? "Riattiva" : "Inattivo"}
            </span>
          </Button>
          <Button
            variant="outline"
            onClick={exportPdfReport}
            disabled={pdfLoading}
            className="border-[#262626] text-zinc-300 hover:bg-[#1A1A1A] hover:text-white gap-2"
            data-testid="export-pdf-report"
          >
            <FilePdf size={18} />
            <span className="hidden sm:inline">{pdfLoading ? "Genero..." : "Esporta PDF"}</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowSharePanel(!showSharePanel)}
            className={`border-[#262626] text-zinc-300 hover:bg-[#1A1A1A] hover:text-white gap-2 ${showSharePanel ? 'bg-[#1A1A1A] text-white' : ''}`}
            data-testid="toggle-share-panel"
          >
            <LinkSimple size={18} />
            <span className="hidden sm:inline">Condividi</span>
          </Button>
        </div>
      </div>

      {/* Share panel */}
      {showSharePanel && (
        <div className="mb-6 bg-[#171717] border border-[#262626] rounded-lg p-5" data-testid="share-panel">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <LinkSimple size={16} className="text-[#007AFF]" />
            LINK CONDIVISIBILE
          </h3>
          {shareToken ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-xs text-zinc-400 truncate select-all" data-testid="share-link-url">
                  {window.location.origin}/shared/{shareToken}
                </div>
                <Button onClick={copyShareLink} size="sm" className="bg-[#007AFF] hover:bg-[#3395FF] text-white px-3 flex-shrink-0" data-testid="copy-share-link">
                  <Copy size={16} />
                </Button>
              </div>
              <p className="text-[11px] text-zinc-600">
                Il cliente potrà vedere programmi, schede, misurazioni e libreria esercizi in sola lettura.
              </p>
              <div className="flex gap-2">
                <Button onClick={generateShareLink} disabled={shareLoading} variant="outline" size="sm" className="border-[#262626] text-zinc-400 hover:bg-[#1A1A1A] hover:text-white gap-1.5" data-testid="regenerate-share-link">
                  <ArrowsClockwise size={14} /> Rigenera
                </Button>
                <Button onClick={revokeShareLink} disabled={shareLoading} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 gap-1.5" data-testid="revoke-share-link">
                  <Trash size={14} /> Revoca
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-xs text-zinc-500 mb-3">
                Genera un link unico da condividere con il cliente. Il link dà accesso in sola lettura a programmi, schede, misurazioni e libreria esercizi.
              </p>
              <Button onClick={generateShareLink} disabled={shareLoading} className="bg-[#007AFF] hover:bg-[#3395FF] text-white gap-2" data-testid="generate-share-link">
                {shareLoading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <LinkSimple size={16} />
                )}
                Genera Link
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="bg-[#171717] border border-[#262626] p-1 mb-6 flex-wrap h-auto" data-testid="client-tabs">
          <TabsTrigger value="panoramica" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white text-zinc-400 text-sm" data-testid="tab-panoramica">Panoramica</TabsTrigger>
          <TabsTrigger value="anamnesi" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white text-zinc-400 text-sm" data-testid="tab-anamnesi">Anamnesi</TabsTrigger>
          <TabsTrigger value="misure" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white text-zinc-400 text-sm" data-testid="tab-misure">Misure</TabsTrigger>
          <TabsTrigger value="fasi" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white text-zinc-400 text-sm" data-testid="tab-fasi">Fasi</TabsTrigger>
          <TabsTrigger value="schede" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white text-zinc-400 text-sm" data-testid="tab-schede">Schede</TabsTrigger>
          <TabsTrigger value="logbook" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white text-zinc-400 text-sm" data-testid="tab-logbook">Logbook</TabsTrigger>
          <TabsTrigger value="checkin" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white text-zinc-400 text-sm" data-testid="tab-checkin">Check-in</TabsTrigger>
          <TabsTrigger value="badges" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white text-zinc-400 text-sm" data-testid="tab-badges">Badge</TabsTrigger>
          <TabsTrigger value="galleria" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white text-zinc-400 text-sm" data-testid="tab-galleria">Galleria</TabsTrigger>
        </TabsList>

        <TabsContent value="panoramica">
          <OverviewTab clientId={clientId} client={client} />
        </TabsContent>
        <TabsContent value="anamnesi">
          <AnamnesiTab client={client} onUpdate={fetchClient} />
        </TabsContent>
        <TabsContent value="misure">
          <MeasurementsTab clientId={clientId} />
        </TabsContent>
        <TabsContent value="fasi">
          <PhasesTab clientId={clientId} />
        </TabsContent>
        <TabsContent value="schede">
          <WorkoutsTab clientId={clientId} />
        </TabsContent>
        <TabsContent value="logbook">
          <LogbookTab clientId={clientId} />
        </TabsContent>
        <TabsContent value="checkin">
          <CheckinTab clientId={clientId} />
        </TabsContent>
        <TabsContent value="badges">
          <BadgesTab clientId={clientId} />
        </TabsContent>
        <TabsContent value="galleria">
          <PhotosTab clientId={clientId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
